import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import Utils from '../../utils';
import UserAgentUtils from '../../utils/userAgent';
import NativeApi from '../../utils/nativeApi';
import { defaultTemplate } from '../../utils/styles-constants';
import { StoreState } from '../../reducers';
import { AppInfosState, AppType } from './reducer';
import { MultiAppAuthenticationInfosState, AspConnexionStatus } from '../MultiAppAuthentication/reducer';
import NotificationBar from '../NotificationBar';
import { AppContainer, AppInnerContainer, CustomCSS } from './styles';
import { initializeApp } from './actions';
import CookieNotification from '../../components/CookieNotification';
import Gallery from '../Gallery';
import { GalleryInfosState } from '../Gallery/reducer';
import MultiAppAuthentication from '../MultiAppAuthentication';
import { checkActiveSession } from '../MultiAppAuthentication/actions';
import { AbstractDriveItemListPageParams } from '../../containers/DriveItemListPage/abstract';
import { ProfileState } from '../../containers/ProfilePage/reducer';
import RoutesUtils from '../../utils/routes';
import { RouterParamsState } from '../SetRouteParams/reducer';
import { injectCustomJS, removeCustomJS } from '../../utils/customJs';

const svgSpritePath = require('../../images/icons.svg');

export interface AppProps {
	children: React.ReactNode;
	appInfos: AppInfosState;
	multiAppAuthenticationInfos: MultiAppAuthenticationInfosState;
	galleryInfos: GalleryInfosState;
	profile: ProfileState;
	locale: string;
	dispatch: Dispatch<StoreState>;
	params?: AbstractDriveItemListPageParams;
	routerParamsHistory: RouterParamsState[];
}

export interface AppState {
	svgSprites: string;
	hasStatusBar: boolean;
}

class App extends React.Component<AppProps, AppState> {

	constructor(props: AppProps) {
		super(props);
		this.state = {
			svgSprites: '',
			hasStatusBar: false
		};
	}

	componentDidMount() {

		if (UserAgentUtils.isMobileApp) {
			this.handleStatusBar();
		}

		this.props.dispatch(initializeApp());

		Utils.promiseXHR('GET', svgSpritePath, undefined, undefined, undefined, undefined, undefined, true)
			.then(response => this.setState({ svgSprites: response.responseText }));

		document.addEventListener('visibilitychange', this.handleVisibilityChange);
		this.syncCustomJS(this.props.galleryInfos.customJS);
	}

	componentDidUpdate(prevProps: AppProps) {
		if (prevProps.galleryInfos.customJS !== this.props.galleryInfos.customJS) {
			this.syncCustomJS(this.props.galleryInfos.customJS);
		}
	}

	componentWillUnmount() {
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
		removeCustomJS();
	}

	syncCustomJS = (customJS: string) => {
		injectCustomJS(customJS);
	}

	render() {
		const {
			children,
			galleryInfos,
			appInfos,
			profile,
			multiAppAuthenticationInfos,
			params,
			locale,
			routerParamsHistory,
		} = this.props;
		const { svgSprites, hasStatusBar } = this.state;
		const { template, customCSS } = galleryInfos;
		const theme = template && template.commonStyles ? template.commonStyles : defaultTemplate.commonStyles;
		const { type } = appInfos;
		const { aspConnexionStatus } = multiAppAuthenticationInfos;
		const isConnectedToAsp = aspConnexionStatus === AspConnexionStatus.SUCCESS;
		const { isMobileApp } = UserAgentUtils;
		const shouldDisplayMultiAppAuthentication = isMobileApp && type === AppType.Multiple && !isConnectedToAsp;

		let AppInnerContainerClass = `AppInnerContainer ${locale}`;

		if (params && params.origin) {
			AppInnerContainerClass += ` origin-${params.origin}`;
		}
		if (params && params.originParam && params.origin !== 'search') {
			AppInnerContainerClass += ` originParam-${params.originParam}`;
		}
		if (profile && profile.id) {
			AppInnerContainerClass += ` user-${profile.id}`;
		}
		if (profile && profile.role) {
			AppInnerContainerClass += ` user-${profile.role}`;
		}

		AppInnerContainerClass += RoutesUtils.getAppInnerContainerPageClasses(routerParamsHistory);

		return (
			<ThemeProvider theme={theme}>
				<>
					<CustomCSS customCSS={customCSS} />
					<AppContainer hasStatusBar={hasStatusBar}>
						{type &&
							<AppInnerContainer className={AppInnerContainerClass}>
								{shouldDisplayMultiAppAuthentication ?
									<MultiAppAuthentication />
									:
									<Gallery>
										{children}
									</Gallery>
								}
							</AppInnerContainer>
						}
						{!!svgSprites &&
							<div className="svgSprites" dangerouslySetInnerHTML={{__html: svgSprites}} />
						}
						<NotificationBar />
						<CookieNotification />
					</AppContainer>
				</>
			</ThemeProvider>
		);
	}

	handleStatusBar = () => {
		setTimeout(() => { this.requestHasStatusBar(); }, 250);
	}

	requestHasStatusBar = () => {
		NativeApi.hasStatusBar()
			.then(hasStatusBar => {
				if (typeof hasStatusBar === 'boolean') {
					this.setState({ hasStatusBar });
				} else {
					this.handleStatusBar();
				}
			})
			.catch(() => this.handleStatusBar());
	}

	handleVisibilityChange = () => {
		const { galleryInfos: { isConfigLoaded }} = this.props;

		if (UserAgentUtils.isMobileApp && !document.hidden && isConfigLoaded) {
			this.props.dispatch(checkActiveSession(true));
		}
	}
}

const mapStateToProps = (state: StoreState) => {
	const {
		appInfos,
		galleryInfos,
		multiAppAuthenticationInfos,
		profile,
		i18n: { locale },
		routerParamsHistory,
	} = state;
	return {
		appInfos,
		galleryInfos,
		multiAppAuthenticationInfos,
		profile,
		locale,
		routerParamsHistory,
	};
};

export default connect(mapStateToProps)(App);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/App/index.tsx